/*
 * Copyright 2019-2025 Bloomreach
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *   https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { PropsWithChildren } from 'react';
import { Component, isContainer, isContainerItem, Page } from '@bloomreach/spa-sdk';
import { BrMeta } from '../meta';
import { BrNodeContainer } from './BrNodeContainer';
import { BrNodeContainerItem } from './BrNodeContainerItem';
import { BrNodeComponent } from './BrNodeComponent';
import { BrMapping } from '.';

export interface BrNodeProps extends PropsWithChildren {
  /**
   * The brXM component instance containing component-specific data,
   * configuration, and metadata from the Bloomreach Experience Manager.
   */
  component: Component;

  /**
   * The current page instance from the Bloomreach Page Model API.
   * Contains all page-level data, metadata, and configuration needed
   * for rendering and preview mode integration.
   */
  page: Page;

  /**
   * Component mapping object that defines how brXM component types
   * are mapped to React components. Used for dynamic component resolution
   * during page rendering.
   */
  mapping: BrMapping;
}

/**
 * Core node component for rendering brXM components with prop drilling.
 */
export function BrNode({
  children,
  component,
  page,
  mapping,
}: BrNodeProps): React.ReactElement {
  if (React.Children.count(children)) {
    return (
      <BrMeta meta={component.getMeta()}>
        {children}
      </BrMeta>
    );
  }

  const props = {
    page,
    mapping,
  };

  // Pass down all props to child components
  const childrenList = component
    .getChildren()
    .map((child) => (
      <BrNode key={child.getId()} component={child} {...props} />
    ));

  if (isContainer(component)) {
    return (
      <BrNodeContainer component={component} {...props}>
        {childrenList}
      </BrNodeContainer>
    );
  }

  if (isContainerItem(component)) {
    return (
      <BrNodeContainerItem component={component} {...props}>
        {childrenList}
      </BrNodeContainerItem>
    );
  }

  return (
    <BrNodeComponent component={component} {...props}>
      {childrenList}
    </BrNodeComponent>
  );
}
